import axios from "axios";

export const TOKEN_KEY = "voocly_auth_token";
export const USER_KEY = "voocly_auth_user";
export const REMEMBER_KEY = "voocly_auth_remember";
export const EXPIRES_AT_KEY = "voocly_auth_expires_at";
export const SESSION_ONLY_KEY = "voocly_auth_session_only";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

let refreshPromise = null;

export const storageTargets = () => {
  try {
    return [localStorage, sessionStorage];
  } catch {
    return [];
  }
};

export const clearAuthStorage = () => {
  storageTargets().forEach((storage) => {
    storage.removeItem(TOKEN_KEY);
    storage.removeItem(USER_KEY);
    storage.removeItem(REMEMBER_KEY);
    storage.removeItem(EXPIRES_AT_KEY);
    storage.removeItem(SESSION_ONLY_KEY);
  });
};

export const hasLocalUser = () => {
  try {
    return !!(localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY));
  } catch {
    return false;
  }
};

const isAuthFlow = (url = "") =>
  [
    "/auth/login",
    "/auth/signup",
    "/auth/refresh",
    "/auth/logout",
    "/auth/forgot-password",
    "/auth/send-otp",
    "/auth/verify-otp",
    "/invite/accept",
  ].some((path) => String(url).includes(path));

// Pages anyone may open without a session (see App.js). An expired session found on one of
// these must not navigate away: an invitee whose browser still remembers an old partner login
// would otherwise land on sign-in instead of the set-password page their email linked to.
const PUBLIC_PATHS = new Set([
  "/", "/signup", "/become-partner", "/forgot-password", "/invite", "/faqs", "/contact",
  "/partner-legal-center", "/about", "/blog", "/safety",
]);

export const isPublicPath = (pathname = window.location.pathname) =>
  PUBLIC_PATHS.has(pathname.replace(/\/+$/, "") || "/");

const redirectToLogin = () => {
  clearAuthStorage();
  window.dispatchEvent(new Event("voocly:auth"));
  // "/" is the sign-in page (there is no "/login" route).
  if (!isPublicPath()) window.location.replace("/");
};

const refreshSession = () => {
  refreshPromise ??= axios
    .post(`${API_BASE}/auth/refresh`, null, { _skipAuthRefresh: true })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};

export const installAuthRefreshInterceptor = (client) => {
  if (client.__vooclyAuthRefreshInstalled) return;
  client.__vooclyAuthRefreshInstalled = true;

  client.interceptors.response.use(
    (res) => res,
    async (err) => {
      const original = err.config || {};
      if (
        err.response?.status !== 401 ||
        original._retryAuthRefresh ||
        original._skipAuthRefresh ||
        isAuthFlow(original.url) ||
        !hasLocalUser()
      ) {
        return Promise.reject(err);
      }

      original._retryAuthRefresh = true;
      try {
        await refreshSession();
        window.dispatchEvent(new Event("voocly:auth"));
        return client(original);
      } catch (refreshErr) {
        redirectToLogin();
        return Promise.reject(refreshErr);
      }
    },
  );
};

// Keep idle portal pages from displaying a session whose membership was removed.
export const startSupplierSessionMonitor = () => {
  let active = true;
  let checking = false;
  const controller = new AbortController();
  const check = async () => {
    if (!active || checking || !hasLocalUser() || document.visibilityState === "hidden") return;
    checking = true;
    try {
      await axios.get(`${API_BASE}/auth/session`, { signal: controller.signal });
    } catch (err) {
      // The refresh interceptor normally ends the session. This also handles a
      // 401 after retrying; network errors should not sign out an active user.
      if (active && err.response?.status === 401 && hasLocalUser()) redirectToLogin();
    } finally {
      checking = false;
    }
  };
  check();
  const timer = setInterval(check, 5000);
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", check);
  return () => {
    active = false;
    controller.abort();
    clearInterval(timer);
    window.removeEventListener("focus", check);
    document.removeEventListener("visibilitychange", check);
  };
};
